import { useState, useEffect, useCallback, useRef } from 'react';
import MLBBetaDashboard from '@/components/mlb-beta/MLBBetaDashboard';
import MLBBetaSchedule from '@/components/mlb-beta/MLBBetaSchedule';
import MLBBetaLeaderboard from '@/components/mlb-beta/MLBBetaLeaderboard';
import MLBBetaPlayers from '@/components/mlb-beta/MLBBetaPlayers';
import MLBBetaPlayersMobile from '@/components/mlb-beta/MLBBetaPlayersMobile';
import MLBBetaMatchups from '@/components/mlb-beta/MLBBetaMatchups';
import MLBBetaDraft from '@/components/mlb-beta/MLBBetaDraft';
import MLBBetaRosters from '@/components/mlb-beta/MLBBetaRosters';
import MLBBetaMyTeam from '@/components/mlb-beta/MLBBetaMyTeam';
import MLBBetaStandings from '@/components/mlb-beta/MLBBetaStandings';
import MLBBetaFantasySchedule from '@/components/mlb-beta/MLBBetaFantasySchedule';
import MLBBetaSettings from '@/components/mlb-beta/MLBBetaSettings';
import MLBBetaSabotage from '@/components/mlb-beta/MLBBetaSabotage';
import MLBBetaNav from '@/components/mlb-beta/MLBBetaNav';
import MLBBetaBottomNav from '@/components/mlb-beta/MLBBetaBottomNav';
import MLBBetaLanding from '@/components/mlb-beta/MLBBetaLanding';
import MLBBetaPlayerDetail from '@/components/mlb-beta/MLBBetaPlayerDetail';
import MLBBetaTransactionHistory from '@/components/mlb-beta/MLBBetaTransactionHistory';
import MLBBetaTeamDetail from '@/components/mlb-beta/MLBBetaTeamDetail';
import NotificationBell from '@/components/NotificationBell';
import UserAccountMenu from '@/components/UserAccountMenu';
import BetaCreditsDropdown from '@/components/BetaCreditsDropdown';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { useSport } from '@/contexts/SportContext';
import { useLeague } from '@/contexts/LeagueContext';
import { MLBLeagueSwitcher } from '@/components/mlb-beta/MLBLeagueSwitcher';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Settings } from 'lucide-react';
import { ScoringRulesProvider } from '@/contexts/ScoringRulesContext';
import { useDraftRoomLauncher } from '@/hooks/useDraftRoomLauncher';
import MobileHamburgerMenu from '@/components/MobileHamburgerMenu';
import { useLiveDraft } from '@/hooks/useLiveDraft';
import { Capacitor } from '@capacitor/core';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsSmallViewport } from '@/hooks/useIsSmallViewport';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { trackEvent } from '@/lib/analytics';

type MLBBetaTab = 'dashboard' | 'schedule' | 'leaderboard' | 'players' | 'matchups' | 'rosters' | 'myteam' | 'standings' | 'fantasy-schedule' | 'draft' | 'sabotage' | 'settings' | 'player-detail' | 'transactions' | 'team-detail';

const MLBBeta = () => {
  const [activeTab, setActiveTabRaw] = useState<MLBBetaTab>('dashboard');
  const setActiveTab = useCallback((tab: MLBBetaTab) => {
    setActiveTabRaw(tab);
    trackEvent('page_view', { page: tab });
  }, []);
  const [notifOpen, setNotifOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [settingsResetKey, setSettingsResetKey] = useState(0);
  const [userLeagues, setUserLeagues] = useState<{ id: string; name: string }[]>([]);
  // Distinguishes "user has zero memberships" from "memberships still loading".
  // Used to gate the !hasLeague Landing render so authenticated users with leagues
  // never flash through MLBBetaLanding's Create/Join CTAs during LeagueContext resolution.
  const [leaguesFetched, setLeaguesFetched] = useState(false);
  // SAB-592: shell-triggered create/join, so a user who already has an MLB league
  // can join or create another from the switcher (not only from the empty state).
  const [leagueAction, setLeagueAction] = useState<'create' | 'join' | null>(null);
  const { user } = useAuth();
  const { setSport } = useSport();
  const { hasLeague, activeLeague, setActiveLeagueId, refreshLeague, isCommissioner, isLoading: leagueLoading } = useLeague();
  const onDraftClosed = useCallback(() => {
    refreshLeague();
  }, [refreshLeague]);
  useDraftRoomLauncher(onDraftClosed);
  const { draft } = useLiveDraft();
  const isMobile = useIsMobile();
  // SAB-352 Phase 4 — sub-640 dispatches MLBBetaPlayersMobile; sm+ stays on desktop.
  const isSmallViewport = useIsSmallViewport();
  // On desktop, provide a direct URL so nav/dashboard can render <a> tags (never blocked by popup blocker)
  const draftRoomUrl = activeLeague?.id && !isMobile && !Capacitor.isNativePlatform()
    ? `/draft-room/${activeLeague.id}`
    : undefined;
  usePushNotifications();
  useEffect(() => {
    setSport('mlb');
  }, [setSport]);

  // Fetch user's leagues for switcher
  useEffect(() => {
    if (!user) return;
    const fetchLeagues = async () => {
      const { data } = await supabase
        .from('league_members')
        .select('league_id, leagues(id, name)')
        .eq('user_id', user.id);
      if (data) {
        setUserLeagues(
          data
            .filter((d: any) => d.leagues)
            .map((d: any) => ({ id: d.leagues.id, name: d.leagues.name }))
        );
      }
      setLeaguesFetched(true);
    };
    fetchLeagues();
  }, [user]);

  // Recovery — if LeagueContext failed to auto-resolve an active league but the
  // user clearly has memberships, force-select the first one. Prevents users
  // with leagues from being stranded on the Landing page when sf_active_league_id
  // is stale or the LeagueContext membership query had a transient failure.
  //
  // One-shot guard: if the recovery's setActiveLeagueId triggers a refreshLeague
  // that fails membership validation (clearing activeLeagueId back to null),
  // we'd loop. The ref guard fires recovery once per fresh memberships fetch.
  // Reset condition: userLeagues length transitions 0 → N, indicating a new
  // fetch worth retrying (e.g., user just joined a league via MLBBetaLanding).
  const recoveryAttempted = useRef(false);
  const lastUserLeaguesLen = useRef(0);
  useEffect(() => {
    if (lastUserLeaguesLen.current === 0 && userLeagues.length > 0) {
      recoveryAttempted.current = false;
    }
    lastUserLeaguesLen.current = userLeagues.length;

    if (!leaguesFetched || leagueLoading || hasLeague || userLeagues.length === 0) return;
    if (recoveryAttempted.current) return;
    recoveryAttempted.current = true;
    setActiveLeagueId(userLeagues[0].id);
  }, [leaguesFetched, leagueLoading, hasLeague, userLeagues, setActiveLeagueId]);

  const handleLeagueReady = (leagueId: string) => {
    setActiveLeagueId(leagueId);
    refreshLeague();
  };

  // Show splash while auth, league data, OR the user's memberships are still loading.
  // Gating on leaguesFetched too prevents the Landing flash when LeagueContext
  // hasn't yet resolved an active league for a user who has memberships.
  if (leagueLoading || !leaguesFetched) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-14 w-auto animate-pulse" />
      </div>
    );
  }

  // Still loading the active league for a user who has memberships — keep splash
  // up rather than render Landing. The recovery effect above will trigger
  // setActiveLeagueId on the next pass; this branch covers the gap between.
  if (!hasLeague && userLeagues.length > 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-14 w-auto animate-pulse" />
      </div>
    );
  }

  // Show landing page only when memberships fetch confirmed user has zero leagues.
  if (!hasLeague) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
          <div className="container mx-auto px-4 flex items-center justify-between h-14 max-w-5xl">
            <div className="flex items-center gap-2">
              <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-8 w-auto" />
            </div>
          </div>
        </header>
        <main className="flex-1 container mx-auto px-4 py-6 max-w-5xl">
          <MLBBetaLanding onLeagueReady={handleLeagueReady} />
        </main>
      </div>
    );
  }

  // SAB-592: user HAS a league but tapped Join/Create in the switcher. Show the
  // create/join view over the shell; cancel or completion returns to the shell.
  if (leagueAction) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
          <div className="container mx-auto px-4 flex items-center justify-between h-14 max-w-5xl">
            <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-8 w-auto" />
            <button
              onClick={() => setLeagueAction(null)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Back to my league
            </button>
          </div>
        </header>
        <main className="flex-1 container mx-auto px-4 py-6 max-w-5xl">
          <MLBBetaLanding
            initialView={leagueAction}
            onExit={() => setLeagueAction(null)}
            onLeagueReady={(id) => { setLeagueAction(null); handleLeagueReady(id); }}
          />
        </main>
      </div>
    );
  }

  const handleNavigateToPlayer = (pid: string) => {
    setSelectedPlayerId(pid);
    setActiveTab('player-detail');
  };

  const handleNavigateToTeam = (tid: string) => {
    setSelectedTeamId(tid);
    setActiveTab('team-detail');
  };

  const renderContent = () => {
    if (activeTab === 'player-detail' && selectedPlayerId) {
      return (
        <MLBBetaPlayerDetail
          playerId={selectedPlayerId}
          onBack={() => setActiveTab('players')}
          onNavigateToTrades={() => setActiveTab('myteam')}
        />
      );
    }

    if (activeTab === 'team-detail' && selectedTeamId) {
      return (
        <MLBBetaTeamDetail
          teamId={selectedTeamId}
          onBack={() => setActiveTab('myteam')}
          onProposeTrade={() => setActiveTab('myteam')}
        />
      );
    }

    switch (activeTab) {
      case 'schedule': return <MLBBetaSchedule />;
      case 'leaderboard': return <MLBBetaLeaderboard />;
      case 'players': return isSmallViewport
        ? <MLBBetaPlayersMobile onNavigateToTrades={() => setActiveTab('myteam')} onNavigateToPlayer={handleNavigateToPlayer} />
        : <MLBBetaPlayers onNavigateToTrades={() => setActiveTab('myteam')} onNavigateToPlayer={handleNavigateToPlayer} />;
      case 'matchups': return <MLBBetaMatchups />;
      case 'rosters': return <MLBBetaRosters />;
      case 'myteam': return <MLBBetaMyTeam onNavigateToTeam={handleNavigateToTeam} />;
      case 'standings': return <MLBBetaStandings />;
      case 'fantasy-schedule': return <MLBBetaFantasySchedule />;
      case 'draft': return <MLBBetaDraft />;
      case 'sabotage': return <MLBBetaSabotage leagueId={activeLeague?.id || null} />;
      case 'transactions': return <MLBBetaTransactionHistory />;
      case 'settings': return <MLBBetaSettings isCommissioner={isCommissioner} resetKey={settingsResetKey} />;
      default: return <MLBBetaDashboard onNavigate={setActiveTab} draftRoomUrl={draftRoomUrl} />;
    }
  };

  return (
    <ScoringRulesProvider leagueId={activeLeague?.id || null}>
    <div className="min-h-screen bg-background flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        {/* Compact Grid spec: on mobile, header is a 3-column grid (logo | switcher | icons)
            so the league switcher is centered to the viewport, not to the leftover flex space.
            minmax(0,1fr) — not bare 1fr — is required: bare 1fr is shorthand for minmax(auto,1fr),
            and `auto` floors the track to its min-content. The icons cluster (~100px) is wider
            than the logo (~70px), so without minmax(0,...) the two side tracks resolve to their
            different min-contents and the auto-sized title cell drifts off-viewport-center.
            sm+ falls back to flex so the MLBBetaNav can sit alongside the switcher. */}
        <div className="container mx-auto px-3 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center h-14 max-w-6xl gap-3 sm:flex">
          <div className="flex items-center shrink-0 justify-self-start">
            <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-8 w-auto" />
            {/* SAB-575: desktop same-sport league switcher, left-of-brand
                (Sleeper/ESPN pattern). Hidden below lg where the centered one takes over. */}
            <div className="hidden lg:flex items-center min-w-0 ml-3">
              <MLBLeagueSwitcher userLeagues={userLeagues} onLeagueAction={setLeagueAction} />
            </div>
          </div>
          {/* Mobile/tablet league name + switcher (centered; hidden on desktop) */}
          <div className="flex lg:hidden items-center flex-1 justify-center min-w-0 justify-self-center">
            <MLBLeagueSwitcher userLeagues={userLeagues} onLeagueAction={setLeagueAction} />
          </div>
          {/* Top nav — visible on tablet+ (≥640px), hidden on small mobile */}
          <div className="hidden sm:flex flex-1 items-center justify-center">
            <MLBBetaNav activeTab={activeTab === 'player-detail' ? 'players' : activeTab === 'transactions' ? 'dashboard' : activeTab === 'team-detail' ? 'myteam' : activeTab} onTabChange={(t) => setActiveTab(t)} onOpenNotifications={() => setNotifOpen(true)} draftRoomUrl={draftRoomUrl} />
          </div>
          <div className="flex items-center gap-1 shrink-0 justify-self-end">
            <button
              onClick={() => { setActiveTab('settings'); setSettingsResetKey(k => k + 1); }}
              className="hidden sm:flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Settings"
            >
              <Settings className="h-5 w-5" />
            </button>
            <UserAccountMenu />
            {/* BetaCredits hidden on mobile — same panel is duplicated inside MobileHamburgerMenu
                (Coins icon + Sabotage Credits + ${totalCredits} badge). Trimming the header
                cluster lets the title true-center via grid-cols-[1fr_auto_1fr] on mobile. */}
            <div className="hidden sm:flex">
              <BetaCreditsDropdown />
            </div>
            <NotificationBell dialogOpen={notifOpen} onDialogOpenChange={setNotifOpen} />
            <MobileHamburgerMenu activeTab={activeTab} onTabChange={(t) => { setActiveTab(t as MLBBetaTab); if (t === 'settings') setSettingsResetKey(k => k + 1); }} />
          </div>
        </div>
      </header>

      {/* Content — bottom padding clears the fixed mobile bottom nav (≤640px) and respects iOS safe-area */}
      <main className="flex-1 container mx-auto px-4 py-4 max-w-5xl pb-[calc(env(safe-area-inset-bottom)+64px)] sm:pb-6">
        {renderContent()}
      </main>

      <MLBBetaBottomNav activeTab={activeTab} onTabChange={(t) => setActiveTab(t as MLBBetaTab)} />
    </div>
    </ScoringRulesProvider>
  );
};

export default MLBBeta;
